import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, Eye, FileText, Landmark, Link2, Loader2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { creditSense } from "@/lib/api";
import { KIND_LABEL, LinkPill, StatusPill, fmtDate, fmtTs, money, openBlob } from "./creditSenseShared";
import { CreditSenseAttachDialog } from "./CreditSenseAttachDialog";

const Row = ({ k, v, mono = false }) => (
  <div className="flex gap-2 py-0.5 text-xs">
    <span className="w-32 shrink-0 text-slate-500">{k}</span>
    <span className={`min-w-0 break-words text-slate-900 ${mono ? "font-mono" : ""}`}>{v || <span className="text-slate-300">—</span>}</span>
  </div>
);
const Card = ({ title, children, testId, right }) => (
  <section className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid={testId}>
    <header className="mb-2 flex items-center justify-between"><h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</h3>{right}</header>
    {children}
  </section>
);

function Accounts({ s }) {
  const accts = s.accounts || [];
  if (!accts.length) return <p className="text-xs text-slate-400">No account data in the report yet.</p>;
  return (
    <div className="divide-y" data-testid="creditsense-accounts">
      {accts.map((a, i) => (
        <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 py-1.5 text-xs">
          <Landmark className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-medium text-slate-900">{a.bank || "Bank"}</span>
          <span className="text-slate-700">{a.name}</span>
          <span className="font-mono text-[11px] text-slate-500">{a.masked}</span>
          {a.holder && <span className="text-[11px] text-slate-500">· {a.holder}</span>}
          <span className="ml-auto font-mono tabular-nums">{money(a.balance_current)}</span>
          {a.transactions ? <Badge variant="outline" className="text-[9px]">{a.transactions} txns</Badge> : null}
          {a.authenticity && <Badge variant="outline" className={`text-[9px] ${/pass|verified|ok/i.test(a.authenticity) ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-800"}`}>{a.authenticity}</Badge>}
        </div>
      ))}
    </div>
  );
}

function Files({ d, onOpen }) {
  const [busy, setBusy] = useState(null);
  const act = async (f, download) => {
    setBusy(f.file_id + (download ? "d" : "v"));
    try { openBlob(await creditSense.fileBlob(d.app_id, f.file_id, download), f.name, download); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  if (!d.files?.length) return <p className="text-xs text-slate-400">No files received yet.</p>;
  return (
    <div className="divide-y" data-testid="creditsense-files">
      {d.files.map((f) => (
        <div key={f.file_id} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`creditsense-file-${f.file_id}`}>
          <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className="min-w-0 flex-1 truncate text-slate-900" title={f.name}>{f.name}</span>
          <Badge variant="outline" className="text-[9px]">{KIND_LABEL[f.kind] || f.kind}</Badge>
          <span className="font-mono text-[10px] text-slate-400">{f.size ? `${Math.max(1, Math.round(f.size / 1024))} KB` : ""}</span>
          {["pdf", "png", "html"].includes(f.content_type) && <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => act(f, false)} disabled={!!busy} title="View" data-testid={`creditsense-file-view-${f.file_id}`}>{busy === f.file_id + "v" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}</Button>}
          <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => act(f, true)} disabled={!!busy} title="Download" data-testid={`creditsense-file-download-${f.file_id}`}>{busy === f.file_id + "d" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}</Button>
        </div>
      ))}
    </div>
  );
}

export function CreditSenseDetail({ appId, attachFirst, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [attach, setAttach] = useState(false);
  const load = async () => {
    try { setD(await creditSense.detail(appId)); setErr(null); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { if (appId) { setD(null); load(); setAttach(!!attachFirst); } }, [appId, attachFirst]); // eslint-disable-line react-hooks/exhaustive-deps
  const unlink = async () => {
    try { await creditSense.unlink(appId); toast.success("Unlinked"); await load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const s = d?.summary || {};
  return (
    <Sheet open={!!appId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-3xl" data-testid="creditsense-detail">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2 text-base">
            <Landmark className="h-4 w-4 text-emerald-600" />{d?.applicant || "Credit Sense report"}
            {d && <StatusPill status={d.status} testId="creditsense-detail-status" />}
            {d && <LinkPill linked={d.linked} suggested={d.suggested} onClick={() => setAttach(true)} testId="creditsense-detail-link" />}
          </SheetTitle>
        </SheetHeader>
        {err && <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="creditsense-detail-error">{err}</div>}
        {!d && !err && <div className="p-8 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />Loading…</div>}
        {d && (
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setAttach(true)} data-testid="creditsense-detail-attach-btn"><Link2 className="mr-1 h-3.5 w-3.5" />{d.linked ? "Attach again / elsewhere" : "Attach to application"}</Button>
              {d.linked && <Button size="sm" variant="outline" onClick={unlink} data-testid="creditsense-detail-unlink-btn"><Unlink className="mr-1 h-3.5 w-3.5" />Unlink {d.linked.sf}</Button>}
              {d.status === "error" && <span className="self-center text-xs text-red-700" data-testid="creditsense-detail-errormsg">{d.error_code ? `${d.error_code}: ` : ""}{d.error_message}</span>}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <Card title="Report" testId="creditsense-report-card">
                <Row k="Report" v={s.report_name} />
                <Row k="Statement period" v={s.range_start || s.range_end ? `${fmtDate(s.range_start)} → ${fmtDate(s.range_end)}` : null} />
                <Row k="Banks" v={(s.banks || []).join(", ")} />
                <Row k="Accounts" v={s.accounts_total ? `${s.accounts_total} · total balance ${money(s.balance_total)}` : null} />
                <Row k="Received" v={fmtTs(d.received_at)} />
                <Row k="Credit Sense App ID" v={d.app_id} mono />
                <Row k="Reference" v={d.app_reference} mono />
                <Row k="Store" v={d.store_name} />
              </Card>
              <Card title="Applicant" testId="creditsense-applicant-card">
                <Row k="Name" v={d.applicant} />
                <Row k="Business" v={d.business_name} />
                {(s.customers || []).map((c, i) => <Row key={i} k={i ? "" : "Customers"} v={[c.name, c.email, c.mobile, c.dob && `DOB ${fmtDate(c.dob)}`].filter(Boolean).join(" · ")} />)}
                {(s.decision_points || []).map((p, i) => <Row key={`dp${i}`} k={p.title} v={p.value} />)}
                {d.linked && <Row k="Attached" v={`${d.linked.sf} · ${d.linked.name || ""} · ${fmtTs(d.linked.at)} by ${d.linked.by}${d.linked.ob_set ? " · OB → Connected" : ""}`} />}
              </Card>
            </div>
            <Card title="Accounts" testId="creditsense-accounts-card"><Accounts s={s} /></Card>
            <Card title="Files" testId="creditsense-files-card"><Files d={d} /></Card>
            {d.actions?.length > 0 && (
              <Card title="Portal history" testId="creditsense-history-card">
                {d.actions.map((a, i) => <Row key={i} k={fmtTs(a.at)} v={`${a.action} ${a.sf || ""} · ${a.by}${a.files?.length ? ` · ${a.files.length} file(s)` : ""}${a.error ? ` · ⚠ ${a.error}` : ""}`} />)}
              </Card>
            )}
          </div>
        )}
        {attach && d && <CreditSenseAttachDialog appId={appId} onClose={() => setAttach(false)} onDone={() => { load(); onChanged?.(); }} />}
      </SheetContent>
    </Sheet>
  );
}
